import React, { useState, useRef } from 'react';
import { Calendar, Clock, Download, FileText, Printer, Share2, User, Trash, Upload } from 'lucide-react';
import { RecoveryPlan } from '../RecoveryMonitoring';

interface ProgressReportProps {
  recoveryPlan: RecoveryPlan;
}

// Helper function to format dates
const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

// Function to calculate days between two dates
const daysBetween = (date1: Date, date2: Date): number => {
  const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
  const diffTime = Math.abs(date2.getTime() - date1.getTime());
  return Math.round(diffTime / oneDay);
};

const ProgressReport: React.FC<ProgressReportProps> = ({ recoveryPlan }) => {
  const [reportType, setReportType] = useState<'summary' | 'detailed'>('summary');
  const [hasSignature, setHasSignature] = useState<boolean>(false);
  const [reportStatus, setReportStatus] = useState<'draft' | 'finalized'>('draft');
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [providerInfo, setProviderInfo] = useState({
    name: '',
    title: '',
    date: new Date().toISOString().split('T')[0]
  });
  const signaturePadRef = useRef<HTMLDivElement>(null);
  
  // Get the latest assessment
  const latestAssessment = recoveryPlan.progressSnapshots[recoveryPlan.progressSnapshots.length - 1];
  
  // Get the previous assessment (1 week ago if available)
  const oneWeekAgo = new Date(latestAssessment.date);
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
  
  const previousAssessment = recoveryPlan.progressSnapshots
    .filter(snapshot => snapshot.date <= oneWeekAgo)
    .sort((a, b) => b.date.getTime() - a.date.getTime())[0];
  
  // Calculate changes in metrics
  const calculateMetricChanges = () => {
    if (!previousAssessment) return {};
    
    const changes: { [key: string]: { value: number, percentage: number, improved: boolean } } = {};
    
    Object.keys(latestAssessment.metrics).forEach(metric => {
      const currentValue = latestAssessment.metrics[metric];
      const previousValue = previousAssessment.metrics[metric];
      
      const change = currentValue - previousValue;
      const percentageChange = previousValue === 0 ? 0 : (change / previousValue) * 100;
      
      // For most metrics, higher is better, but for some like stepTime, lower is better
      const inversedMetrics = ['stepTime'];
      const isImproved = inversedMetrics.includes(metric) ? change < 0 : change > 0;
      
      changes[metric] = {
        value: change,
        percentage: percentageChange,
        improved: isImproved
      };
    });
    
    return changes;
  };
  
  const metricChanges = calculateMetricChanges();
  
  // Calculate phase progress
  const calculatePhaseProgress = () => {
    const currentPhase = recoveryPlan.phases[recoveryPlan.currentPhase];
    const phaseMetrics = Object.keys(currentPhase.targetMetrics);
    
    let totalProgress = 0;
    
    phaseMetrics.forEach(metric => {
      const currentValue = latestAssessment.metrics[metric];
      const targetValue = currentPhase.targetMetrics[metric];
      const initialValue = recoveryPlan.progressSnapshots
        .find(s => s.phase === recoveryPlan.currentPhase)?.metrics[metric] || 0;
      
      // Skip if target and initial are the same (avoid division by zero)
      if (targetValue === initialValue) return;
      
      // Calculate progress towards target
      const progress = (currentValue - initialValue) / (targetValue - initialValue);
      totalProgress += Math.min(1, Math.max(0, progress)); // Clamp between 0 and 1
    });
    
    return Math.round((totalProgress / phaseMetrics.length) * 100);
  };
  
  const phaseProgress = calculatePhaseProgress();
  
  // Calculate time in current phase
  const daysInCurrentPhase = daysBetween(
    recoveryPlan.phases[recoveryPlan.currentPhase].startDate || recoveryPlan.recoveryStart,
    new Date()
  );
  
  // Format metric name to be more readable
  const formatMetricName = (name: string): string => {
    return name
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase());
  };
  
  // Generate recommendations based on metrics
  const generateRecommendations = () => {
    const recommendations: string[] = [];
    
    // Check for metrics that are significantly behind
    if (previousAssessment) {
      Object.keys(metricChanges).forEach(metric => {
        const change = metricChanges[metric];
        if (!change.improved && Math.abs(change.percentage) > 5) {
          recommendations.push(
            `Focus on improving ${formatMetricName(metric)}, which has decreased by ${Math.abs(change.percentage).toFixed(1)}% since the last assessment.`
          );
        }
      });
    }
    
    // If in phase for too long, suggest intervention
    const expectedPhaseDuration = 30; // days, assumption
    if (daysInCurrentPhase > expectedPhaseDuration * 1.5) {
      recommendations.push(
        `Patient has been in ${recoveryPlan.phases[recoveryPlan.currentPhase].name} phase for ${daysInCurrentPhase} days, which is longer than expected. Consider reviewing and potentially adjusting the treatment plan.`
      );
    }
    
    // Based on pain level
    if (latestAssessment.patientReported.painLevel > 5) {
      recommendations.push(
        `Patient reports pain level of ${latestAssessment.patientReported.painLevel}/10. Consider pain management strategies.`
      );
    }
    
    // If nearly complete with phase
    if (phaseProgress > 90 && recoveryPlan.currentPhase < recoveryPlan.phases.length - 1) {
      recommendations.push(
        `Patient is ${phaseProgress}% through current recovery phase. Consider evaluation for progression to the next phase.`
      );
    }
    
    // If no specific recommendations, provide a general one
    if (recommendations.length === 0) {
      recommendations.push(
        'Continue with the current rehabilitation program. Patient is making expected progress.'
      );
    }
    
    return recommendations;
  };
  
  const recommendations = generateRecommendations();
  
  // Handle provider info changes
  const handleProviderInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    const field = id.replace('provider-', '');
    setProviderInfo({
      ...providerInfo,
      [field]: value
    });
  };
  
  // Handle signature pad click
  const handleSignaturePadClick = () => {
    // In a real implementation, this would initialize a signature drawing canvas
    // For this demo, we'll simulate having a signature
    setHasSignature(true);
    
    if (signaturePadRef.current) {
      signaturePadRef.current.innerHTML = '<div class="text-center"><p class="font-signature text-lg text-blue-600">Jane Smith, DPT</p><p class="text-xs text-gray-500 mt-1">Digitally signed on ' + new Date().toLocaleDateString() + '</p></div>';
    }
  };
  
  // Clear signature
  const handleClearSignature = () => {
    setHasSignature(false);
    
    if (signaturePadRef.current) {
      signaturePadRef.current.innerHTML = '<p class="text-gray-400 text-center text-sm">Click to sign or upload signature image</p>';
    }
  };
  
  // Simulate file upload
  const handleUploadSignature = () => {
    // In a real implementation, this would open a file dialog
    // For this demo, we'll simulate having uploaded a signature
    setHasSignature(true);
    
    if (signaturePadRef.current) {
      signaturePadRef.current.innerHTML = '<div class="text-center"><p class="italic font-medium text-lg text-blue-600">Uploaded Signature</p><p class="text-xs text-gray-500 mt-1">Uploaded on ' + new Date().toLocaleDateString() + '</p></div>';
    }
  };
  
  // Finalize report with signature
  const handleFinalizeReport = () => {
    if (!hasSignature) {
      alert('Please add your signature before finalizing the report.');
      return;
    }
    
    if (!providerInfo.name.trim()) {
      alert('Please enter your name before finalizing the report.');
      return;
    }
    
    // In a real implementation, this would generate a PDF with the signature
    setReportStatus('finalized');
    setShowConfirmation(true);
    
    // Hide confirmation after 5 seconds
    setTimeout(() => {
      setShowConfirmation(false);
    }, 5000);
  };

  return (
    <div className="p-6">
      {/* Report Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-lg font-medium">Recovery Progress Report</h3>
          <p className="text-sm text-gray-600">
            Generated on {formatDate(new Date())} | Patient: {recoveryPlan.patientName}
          </p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setReportType('summary')}
            className={`px-3 py-1 text-sm rounded ${
              reportType === 'summary' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
            }`}
          >
            Summary
          </button>
          <button
            onClick={() => setReportType('detailed')}
            className={`px-3 py-1 text-sm rounded ${
              reportType === 'detailed' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
            }`}
          >
            Detailed
          </button>
        </div>
      </div>
      
      {/* Success confirmation message */}
      {showConfirmation && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center justify-between">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="text-green-700">
              Report successfully finalized and signed by {providerInfo.name}. It is now ready for distribution.
            </span>
          </div>
          <button 
            onClick={() => setShowConfirmation(false)}
            className="text-green-700 hover:text-green-900"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
      
      {/* Report Actions */}
      <div className="bg-white rounded-lg border p-4 mb-6 flex flex-wrap gap-2">
        <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          <Download className="mr-2" size={16} />
          Download Report
        </button>
        <button className="flex items-center px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700">
          <Printer className="mr-2" size={16} />
          Print Report
        </button>
        <button className="flex items-center px-4 py-2 border border-gray-300 rounded hover:bg-gray-50">
          <Share2 className="mr-2" size={16} />
          Share with Provider
        </button>
        
        {reportStatus === 'finalized' && (
          <div className="ml-auto flex items-center">
            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full flex items-center">
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Finalized & Signed
            </span>
          </div>
        )}
        {reportStatus === 'draft' && (
          <div className="ml-auto flex items-center">
            <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full">
              Draft
            </span>
          </div>
        )}
      </div>
      
      {/* Report Content */}
      <div className="bg-white rounded-lg border mb-6 relative">
        {reportStatus === 'finalized' && (
          <div className="absolute top-4 right-4 rotate-[-20deg] border-2 border-green-600 rounded px-2 py-1 text-green-700 text-sm font-bold opacity-30 pointer-events-none">
            FINALIZED
          </div>
        )}
        
        {/* Report Header */}
        <div className="p-6 border-b">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <div>
              <h2 className="text-xl font-bold">
                {reportType === 'summary' ? 'Recovery Summary Report' : 'Detailed Progress Report'}
              </h2>
              <div className="flex flex-col sm:flex-row sm:gap-6 text-sm text-gray-600 mt-2">
                <div className="flex items-center">
                  <User size={14} className="mr-1" />
                  Patient: {recoveryPlan.patientName}
                </div>
                <div className="flex items-center">
                  <Calendar size={14} className="mr-1" />
                  Injury Date: {formatDate(recoveryPlan.injuryDate)}
                </div>
                <div className="flex items-center">
                  <Clock size={14} className="mr-1" />
                  Days Since Injury: {daysBetween(recoveryPlan.injuryDate, new Date())}
                </div>
              </div>
            </div>
            <div className="flex items-center bg-blue-50 text-blue-800 px-4 py-2 rounded-lg">
              <div className="text-right">
                <div className="text-sm font-medium">Current Phase</div>
                <div className="text-lg font-bold">{recoveryPlan.phases[recoveryPlan.currentPhase].name}</div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Summary Overview */}
        <div className="p-6 border-b">
          <h3 className="font-medium mb-4">Recovery Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="text-sm text-gray-600">Current Phase Progress</div>
              <div className="text-2xl font-bold text-blue-600">{phaseProgress}%</div>
              <div className="mt-2 w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full" 
                  style={{ width: `${phaseProgress}%` }}
                ></div>
              </div>
            </div>
            
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="text-sm text-gray-600">Time in Current Phase</div>
              <div className="text-2xl font-bold text-indigo-600">{daysInCurrentPhase} days</div>
              <div className="mt-2 text-xs text-gray-500">
                Started on {formatDate(recoveryPlan.phases[recoveryPlan.currentPhase].startDate || recoveryPlan.recoveryStart)}
              </div>
            </div>
            
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="text-sm text-gray-600">Last Assessment</div>
              <div className="text-2xl font-bold text-teal-600">{formatDate(latestAssessment.date)}</div>
              <div className="mt-2 text-xs text-gray-500">
                {daysBetween(latestAssessment.date, new Date())} days ago
              </div>
            </div>
            
            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="text-sm text-gray-600">Patient Reported Pain</div>
              <div className="text-2xl font-bold text-red-600">{latestAssessment.patientReported.painLevel}/10</div>
              <div className="mt-2 w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className={`h-2.5 rounded-full ${
                    latestAssessment.patientReported.painLevel <= 3 ? 'bg-green-600' :
                    latestAssessment.patientReported.painLevel <= 6 ? 'bg-yellow-500' :
                    'bg-red-600'
                  }`}
                  style={{ width: `${(latestAssessment.patientReported.painLevel / 10) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Key Metrics */}
        <div className="p-6 border-b">
          <h3 className="font-medium mb-4">Key Metrics</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Metric
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Current Value
                  </th>
                  {previousAssessment && (
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Previous Value
                    </th>
                  )}
                  {previousAssessment && (
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Change
                    </th>
                  )}
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Target Value
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Progress
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.keys(latestAssessment.metrics).map(metric => {
                  const currentValue = latestAssessment.metrics[metric];
                  const previousValue = previousAssessment ? previousAssessment.metrics[metric] : null;
                  const targetValue = recoveryPlan.phases[recoveryPlan.currentPhase].targetMetrics[metric];
                  const change = previousValue !== null ? metricChanges[metric] : null;
                  
                  // Calculate progress percentage
                  let progress = 0;
                  if (recoveryPlan.currentPhase > 0) {
                    const previousPhaseTarget = recoveryPlan.phases[recoveryPlan.currentPhase - 1].targetMetrics[metric];
                    if (targetValue !== previousPhaseTarget) {
                      progress = Math.min(100, Math.max(0, Math.round(
                        ((currentValue - previousPhaseTarget) / (targetValue - previousPhaseTarget)) * 100
                      )));
                    }
                  } else {
                    const initialValue = recoveryPlan.progressSnapshots[0].metrics[metric];
                    if (targetValue !== initialValue) {
                      progress = Math.min(100, Math.max(0, Math.round(
                        ((currentValue - initialValue) / (targetValue - initialValue)) * 100
                      )));
                    }
                  }
                  
                  return (
                    <tr key={metric}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatMetricName(metric)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {currentValue.toFixed(1)}
                      </td>
                      {previousAssessment && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {previousValue!.toFixed(1)}
                        </td>
                      )}
                      {previousAssessment && (
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            change!.improved ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {change!.value > 0 ? '+' : ''}{change!.value.toFixed(1)} 
                            ({change!.percentage > 0 ? '+' : ''}{change!.percentage.toFixed(1)}%)
                          </span>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {targetValue.toFixed(1)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div 
                            className={`h-2.5 rounded-full ${
                              progress >= 100 ? 'bg-green-600' :
                              progress >= 70 ? 'bg-blue-600' :
                              progress >= 40 ? 'bg-yellow-500' :
                              'bg-red-600'
                            }`}
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-gray-500 mt-1">{progress}%</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Detailed Section - only shown in detailed report */}
        {reportType === 'detailed' && (
          <div className="p-6 border-b">
            <h3 className="font-medium mb-4">Detailed Patient Assessment</h3>
            
            <div className="mb-6">
              <h4 className="text-sm font-medium mb-2">Patient Reported Metrics</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-gray-700">Pain Level</span>
                    <span className="text-xs font-medium bg-red-100 text-red-800 px-2 py-0.5 rounded">
                      {latestAssessment.patientReported.painLevel}/10
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-red-600 h-2 rounded-full" 
                      style={{ width: `${latestAssessment.patientReported.painLevel * 10}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-gray-700">Confidence</span>
                    <span className="text-xs font-medium bg-green-100 text-green-800 px-2 py-0.5 rounded">
                      {latestAssessment.patientReported.confidence}/10
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full" 
                      style={{ width: `${latestAssessment.patientReported.confidence * 10}%` }}
                    ></div>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex justify-between mb-1">
                    <span className="text-sm text-gray-700">Difficulty</span>
                    <span className="text-xs font-medium bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                      {latestAssessment.patientReported.difficulty}/10
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-yellow-500 h-2 rounded-full" 
                      style={{ width: `${latestAssessment.patientReported.difficulty * 10}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mb-6">
              <h4 className="text-sm font-medium mb-2">Current Exercises</h4>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead>
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Exercise</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Frequency</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {recoveryPlan.phases[recoveryPlan.currentPhase].exercises.map(exercise => (
                        <tr key={exercise.id}>
                          <td className="px-4 py-2 text-sm font-medium text-gray-900">{exercise.name}</td>
                          <td className="px-4 py-2 text-sm text-gray-500">{exercise.description}</td>
                          <td className="px-4 py-2 text-sm text-gray-500">{exercise.frequency}</td>
                          <td className="px-4 py-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              exercise.completed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {exercise.completed ? 'Completed' : 'In Progress'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            
            {latestAssessment.notes && (
              <div className="mb-6">
                <h4 className="text-sm font-medium mb-2">Assessment Notes</h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-700">{latestAssessment.notes}</p>
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Recommendations */}
        <div className="p-6">
          <h3 className="font-medium mb-4 flex items-center">
            <FileText className="mr-2 text-blue-500" size={18} />
            Clinical Recommendations
          </h3>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <ul className="space-y-3">
              {recommendations.map((recommendation, index) => (
                <li key={index} className="text-sm text-blue-700">
                  • {recommendation}
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        {/* Provider Signature */}
        <div className="p-6 border-t">
          <h3 className="font-medium mb-4">Healthcare Provider Signature</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="mb-4">
                <label htmlFor="provider-name" className="block text-sm font-medium text-gray-700 mb-1">
                  Provider Name
                </label>
                <input
                  id="provider-name"
                  type="text"
                  className="w-full p-2 border rounded-md"
                  placeholder="Dr. Jane Smith"
                  value={providerInfo.name}
                  onChange={handleProviderInfoChange}
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="provider-title" className="block text-sm font-medium text-gray-700 mb-1">
                  Title/Credentials
                </label>
                <input
                  id="provider-title"
                  type="text"
                  className="w-full p-2 border rounded-md"
                  placeholder="Physical Therapist, DPT"
                  value={providerInfo.title}
                  onChange={handleProviderInfoChange}
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="provider-date" className="block text-sm font-medium text-gray-700 mb-1">
                  Date
                </label>
                <input
                  id="provider-date"
                  type="date"
                  className="w-full p-2 border rounded-md"
                  value={providerInfo.date}
                  onChange={handleProviderInfoChange}
                />
              </div>
            </div>
            
            <div className="flex flex-col">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Signature
              </label>
              <div className={`border-2 ${hasSignature ? 'border-blue-200' : 'border-dashed border-gray-300'} rounded-md h-40 p-2 flex flex-col items-center justify-center bg-gray-50 transition-colors`}>
                <div 
                  ref={signaturePadRef}
                  id="signature-pad" 
                  className="w-full h-full cursor-pointer flex items-center justify-center"
                  onClick={handleSignaturePadClick}
                >
                  <p className="text-gray-400 text-center text-sm">
                    Click to sign or upload signature image
                  </p>
                </div>
              </div>
              <div className="flex mt-2 space-x-2">
                <button 
                  className="px-3 py-1 text-sm border border-gray-300 rounded flex items-center hover:bg-gray-50"
                  onClick={handleClearSignature}
                  disabled={!hasSignature}
                >
                  <Trash size={14} className="mr-1" />
                  Clear
                </button>
                <button 
                  className="px-3 py-1 text-sm border border-gray-300 rounded flex items-center hover:bg-gray-50"
                  onClick={handleUploadSignature}
                >
                  <Upload size={14} className="mr-1" />
                  Upload
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                By signing this report, you certify that the information is accurate and medically appropriate.
              </p>
              <p className="text-xs text-red-500 mt-1 italic">
                Note: For legal reasons, only the physician or physical therapist in charge of the patient's recovery should sign this report.
              </p>
            </div>
          </div>
          
          <div className="mt-6 flex justify-end">
            <button 
              className={`px-4 py-2 ${hasSignature ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400'} text-white rounded-md flex items-center transition-colors`}
              onClick={handleFinalizeReport}
              disabled={!hasSignature}
            >
              <FileText className="mr-2" size={16} />
              Finalize and Sign Report
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgressReport; 